import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import StatusHeader from 'components/common/StatusHeader';
import TargetMenuModal from 'components/TargetMenuModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import masterDataService from 'features/common/masterDataService';
import { useEffect, useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { TARGET_MENU_ORDER_QUANTITY_LIMIT } from 'utils';
import { displayError, handleServiceError } from 'utils/axiosError';
import { MenuOrderQuantityLimitAddPayload, MenuOrderQuantityLimitDeletePayload } from '../models';
import { LimitType } from '../models/courseOrderLimit';
import { menuOrderQuantityLimitService } from '../services';
import ListApplicableCoursesModal from './ListApplicableCourseModal';

function CourseOrderLimit() {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [limitTypeList, setLimitTypeList] = useState<LimitType[]>([]);
  const [selectedLimitType, setSelectedLimitType] = useState<LimitType>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOpenTargetMenuModal, setIsOpenTargetMenuModal] = useState(false);

  // effects
  useEffect(() => {
    const fetchRestrictionsList = async () => {
      try {
        const list = await loadRestrictionsList();
        setLimitTypeList(list['limit_type']);
        if (list['limit_type'] && list['limit_type'].length > 0) {
          setSelectedLimitType(list['limit_type'][0]);
        }
      } catch (error) {
        if (!handleServiceError(showGlobalModal, error as AxiosError)) {
          displayError(showGlobalModal, error as AxiosError);
        }
      }
    };
    fetchRestrictionsList();
    // eslint-disable-next-line
  }, []);

  // functions
  async function loadRestrictionsList() {
    try {
      const restrictions_list = await masterDataService.getCourseOrderLimitType();
      return restrictions_list.data;
    } catch (error) {
      if (!handleServiceError(showGlobalModal, error as AxiosError)) {
        displayError(showGlobalModal, error as AxiosError);
      }
    }
  }

  function handleLoadMenuOrderQuatityLimitType() {
    return menuOrderQuantityLimitService.getList({ limit_type: selectedLimitType?.id });
  }

  function handleDeleteMenuOrderQuatityLimitType(menuIds: number[]) {
    // compose payload
    const payload: MenuOrderQuantityLimitDeletePayload[] = [];
    if (selectedLimitType) {
      menuIds.forEach((menuId) => {
        payload.push({ menu_id: menuId, limit_type: selectedLimitType?.id });
      });
    }
    return menuOrderQuantityLimitService.delete(payload);
  }

  function handleAddMenuOrderQuatityLimitType(menuIds: number[]) {
    // compose payload
    const payload: MenuOrderQuantityLimitAddPayload[] = [];
    if (selectedLimitType) {
      menuIds.forEach((menuId) => {
        payload.push({ menu_id: menuId, limit_type: selectedLimitType?.id });
      });
    }
    return menuOrderQuantityLimitService.add(payload);
  }

  // events
  function handleChangeRestrictionOption(event: React.ChangeEvent<HTMLSelectElement>) {
    const selectedValue = event.target.value;
    const selectedItem = limitTypeList.find((item) => item.id.toString() === selectedValue);
    setSelectedLimitType(selectedItem);
  }

  const handleAddApplicableCoursesModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // TargetMenuModal events
  function handleOpenMenuOrderQuantityLimitType() {
    setIsOpenTargetMenuModal(true);
  }

  function handleCloseMenuOrderQuantityLimitType() {
    setIsOpenTargetMenuModal(false);
  }

  return (
    <div className="page-wrapper">
      <div className="page-header">
        <StatusHeader />
      </div>
      <div className={`page-main ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <div className="page-content store-setting-page d-flex flex-column">
          <div>
            <div className="mb-3">
              <FeatureTitleBar title={t('courseOrderQuantityLimit.title')}></FeatureTitleBar>
            </div>
            <div style={{ marginLeft: '24px', marginTop: '12px' }}>
              {t('courseOrderQuantityLimit.sub_title', { selectedLimitType })}
            </div>
            <div className="d-flex justify-content-center align-items-center ">
              <div className="sticky-table " style={{ minWidth: '500px', marginTop: '20px' }}>
                <Table bordered style={{ borderTop: '1px solid #dee2e6' }}>
                  <tbody>
                    <tr style={{ height: '50px' }}>
                      <td className="text-center" style={{ width: '50%', backgroundColor: '#36489a', color: '#fff' }}>
                        {t('courseOrderQuantityLimitTitle.restrictions')}
                      </td>
                      <td className="text-center">
                        <Form.Select onChange={handleChangeRestrictionOption}>
                          {limitTypeList.map((item, index) => (
                            <option className="text-center" key={index} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                    <tr style={{ height: '50px' }}>
                      <td className="text-center" style={{ backgroundColor: '#36489a', color: '#fff' }}>
                        {t('courseOrderQuantityLimitTitle.course')}
                      </td>
                      <td className="text-center">
                        <div>
                          <Button style={{ minWidth: '90px' }} onClick={handleAddApplicableCoursesModal}>
                            {t('courseOrderQuantityLimitButton.course')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                    <tr style={{ height: '50px' }}>
                      <td className="text-center" style={{ backgroundColor: '#36489a', color: '#fff' }}>
                        {t('courseOrderQuantityLimitTitle.order_quantity_limit_menu')}
                      </td>
                      <td className="text-center">
                        <Button style={{ minWidth: '90px' }} onClick={handleOpenMenuOrderQuantityLimitType}>
                          {t('courseOrderQuantityLimitButton.order_quantity_limit_menu')}
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
              {isModalOpen &&
                selectedLimitType &&
                createPortal(
                  <ListApplicableCoursesModal
                    limitTypeId={selectedLimitType.id}
                    limitTypeName={selectedLimitType.name}
                    onClose={handleCloseModal}
                  />,
                  document.body
                )}
              {isOpenTargetMenuModal &&
                createPortal(
                  <TargetMenuModal
                    targetSourceType={TARGET_MENU_ORDER_QUANTITY_LIMIT}
                    targetName={selectedLimitType?.name}
                    onLoadData={handleLoadMenuOrderQuatityLimitType}
                    onAdd={handleAddMenuOrderQuatityLimitType}
                    onDelete={handleDeleteMenuOrderQuatityLimitType}
                    onClose={handleCloseMenuOrderQuantityLimitType}
                  />,
                  document.body
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseOrderLimit;
